// What a drop MEANS: move, merge, swap, drop-to-ground, or a refusal with a
// reason. Pure arithmetic over plain records, so tools/uikeys.mjs can prove it
// in plain Node — the inventory screen itself needs a browser and a mouse, and
// the previous behaviour (a silent `return` on every awkward drop) is exactly
// what an untested drag handler looks like.
//
// Records are duck-typed: { uid, id, x, y, qty, cond? }. Sizes and stack caps
// arrive as callbacks so this file never imports the item catalog and can be
// exercised with a three-item fake table.

// Do the two rects overlap? Grid cells, half-open ranges.
const hits = (ax, ay, aw, ah, bx, by, bw, bh) =>
  ax < bx + bw && bx < ax + aw && ay < by + bh && by < ay + ah;

// Is `rect` free in this container, ignoring the uids in `skip`?
// NB: the model's own fits() takes a single `ignore` item, which cannot express
// a swap (both items must be lifted at once) — hence a set here.
export function fitsAt(items, gw, gh, rect, skip, sizeOf) {
  const { x, y, w, h } = rect;
  if (x < 0 || y < 0 || x + w > gw || y + h > gh) return false;
  for (const it of items) {
    if (skip && skip.has(it.uid)) continue;
    const [iw, ih] = sizeOf(it.id);
    if (hits(x, y, w, h, it.x, it.y, iw, ih)) return false;
  }
  return true;
}

// Items of `to` that the proposed rect lands on, excluding the dragged item.
function overlapping(to, rect, dragUid, sizeOf) {
  const out = [];
  for (const it of to.items) {
    if (it.uid === dragUid) continue;
    const [iw, ih] = sizeOf(it.id);
    if (hits(rect.x, rect.y, rect.w, rect.h, it.x, it.y, iw, ih)) out.push(it);
  }
  return out;
}

/**
 * Decide what a drop should do.
 *
 * @param item     dragged record
 * @param fromSlot container slot the item is in now (null when it is loose)
 * @param to       { slot, grid:[gw,gh], items:[] } destination container
 * @param x,y      desired top-left cell (the view centre-anchors + clamps)
 * @param sizeOf   (id) -> [w,h]
 * @param stackOf  (id) -> max stack size (1 when the item does not stack)
 * @returns one of
 *   { kind:'none' }                        the item is already there
 *   { kind:'move', x, y }
 *   { kind:'merge', target, qty }          move qty from item into target
 *   { kind:'swap', other }                 exchange the two items' cells
 *   { kind:'deny', reason }                'bounds' | 'occupied' | 'nospace'
 */
export function planDrop({ item, fromSlot, to, x, y, sizeOf, stackOf }) {
  const [gw, gh] = to.grid;
  const [w, h] = sizeOf(item.id);
  if (x < 0 || y < 0 || x + w > gw || y + h > gh) return { kind: 'deny', reason: 'bounds' };
  const sameCont = fromSlot != null && fromSlot === to.slot;
  const rect = { x, y, w, h };
  const over = overlapping(to, rect, item.uid, sizeOf);

  if (!over.length) {
    if (sameCont && item.x === x && item.y === y) return { kind: 'none' };
    return { kind: 'move', x, y };
  }

  // Two or more items under the footprint: there is no single partner to merge
  // with or swap against, so this is a refusal and the player must be told.
  if (over.length > 1) return { kind: 'deny', reason: 'occupied' };

  const other = over[0];
  const cap = stackOf(item.id) || 1;
  if (other.id === item.id && cap > 1 && (other.qty || 1) < cap) {
    const qty = Math.min(item.qty || 1, cap - (other.qty || 1));
    if (qty > 0) return { kind: 'merge', target: other, qty };
  }

  // SWAP — the two items exchange top-left corners. THREE conditions, and the
  // third is the one a 20k-drop brute force caught: with unequal footprints,
  // "both fit where the other was" is NOT enough, because the two destination
  // rects can overlap EACH OTHER. Dragging a 1x1 onto a 2x1 that starts one
  // cell to its right is the minimal case: the 2x1 slides left into the free
  // pair of cells, and its right-hand half lands exactly where the 1x1 just
  // went. DayZ refuses that, and so do we.
  // Cross-container swaps additionally need the model to move a record between
  // two arrays, which the view may not do; see the Inventory.swap() diff in
  // the handoff notes.
  if (!sameCont) return { kind: 'deny', reason: 'occupied' };
  const [ow, oh] = sizeOf(other.id);
  const skip = new Set([item.uid, other.uid]);
  const dragTo = { x: other.x, y: other.y, w, h };
  const otherTo = { x: item.x, y: item.y, w: ow, h: oh };
  const clear = fitsAt(to.items, gw, gh, dragTo, skip, sizeOf)
    && fitsAt(to.items, gw, gh, otherTo, skip, sizeOf)
    && !hits(dragTo.x, dragTo.y, dragTo.w, dragTo.h, otherTo.x, otherTo.y, otherTo.w, otherTo.h);
  if (clear) return { kind: 'swap', other };
  return { kind: 'deny', reason: 'nospace' };
}

// A split halves the stack, the larger half staying put (DayZ rounds the kept
// half up, so splitting a 3 leaves 2 and takes 1). Returns null when there is
// nothing to split.
export function planSplit(item, stackOf) {
  const q = item.qty || 1;
  if (q < 2 || (stackOf(item.id) || 1) < 2) return null;
  const take = Math.floor(q / 2);
  return { take, keep: q - take };
}

// Merging two stacks must never launder condition: the combined stack carries
// the WORSE of the two, or a ruined bandage could be hidden inside a pristine
// pile and healed by the average.
export function mergedCond(a, b) {
  const ca = typeof a === 'number' ? a : 1;
  const cb = typeof b === 'number' ? b : 1;
  return Math.min(ca, cb);
}
